import * as React from 'react'

import { Container, Row, Col } from 'components/Layout'
import Page from 'components/Page'
import PageHeader from 'components/Page/PageHeader'
import HeadingBanner from 'components/Text/HeadingBanner'
import { Crumbs, Crumb } from 'components/BreadCrumbs'
import Button from 'components/Button'
import InfoCard from 'components/Cards/InfoCard'
import StickyChannel from 'components/StickyChannel'
import SimpleTable from 'components/Tables/SimpleTable'
import { Link } from 'react-router'
import LoadingOverlay from 'components/Loading/LoadingOverlay'
import Tooltip from 'components/Tooltip'
import { moment } from 'features/Moment'
import CasesTimeline from 'features/Cases/CasesTimeline'
import NumbersEdit from 'features/Numbers/NumbersEdit'
import { dispatchToProps } from './InternetService.container'
import { useGetInternetServiceQuery } from 'src/generated/graphql'

import cn from 'classnames'
import * as s from './InternetService.scss'
import * as sText from 'components/Text/Text.scss'
import * as sLay from 'styles/modules/layout.scss'
import * as sMarg from 'styles/modules/margins.scss'

export interface IInternetServiceProps {
  params: {
    serviceId: string
  }
  location: {
    query: {
      page?: string
    }
  }
  showCasesCreateTroubleTicket: typeof dispatchToProps['showCasesCreateTroubleTicket']
  showNumbersDisconnectModal: typeof dispatchToProps['showNumbersDisconnectModal']
  children: React.ReactNode
}

export const InternetServiceComponent = (props: IInternetServiceProps) => {
  const { params, showCasesCreateTroubleTicket, showNumbersDisconnectModal, children } = props

  const { data, loading } = useGetInternetServiceQuery({
    variables: { id: params.serviceId },
  })
  if (loading || !data) {
    return (
      <Page layout={{ header: 'app' }} title="Internet">
        <LoadingOverlay transparent />
      </Page>
    )
  }

  const service = data.internetService
  const inactive = service.billingStatus !== 'Active'
  const inactiveMessage = inactive ? 'This service is not active' : ''

  return (
    <Page layout={{ header: 'app' }} title="Internet">
      <PageHeader>
        <Crumbs title="Internet">
          <Crumb parent to="/dashboard/internet">
            Internet
          </Crumb>
          <Crumb
            active={!location.pathname.includes('/usage')}
            to={`/dashboard/internet/${service.id}`}
          >
            {service.number}
          </Crumb>
          <Crumb
            active={location.pathname.includes('/usage')}
            to={`/dashboard/internet/${service.id}/usage`}
          >
            Usage
          </Crumb>
        </Crumbs>
        <HeadingBanner>
          <h3 className={cn(sText.heading3, sText.accent)}>
            Internet Service: {service.usagePlan}
          </h3>
        </HeadingBanner>
      </PageHeader>
      <Container className="md">
        <div className={s.formSection}>
          {children || (
            <Row className={cn(sLay['layout-xs-column'], sLay['layout-gt-xs-row'], 'md')}>
              <Col className={cn('md', sLay['flex-gt-xs-60'])}>
                <InfoCard className={sMarg.mb30to15}>
                  <NumbersEdit service={service} />
                </InfoCard>
                <InfoCard title="Billing Details" className={sMarg.mb30to15}>
                  <SimpleTable>
                    <div>
                      <div>Billing Account:</div>
                      <div>
                        <Link to={`/dashboard/billing/accounts/${service.ban}`}>{service.ban}</Link>
                      </div>
                    </div>
                    <div>
                      <div>Billing Status:</div>
                      <div>{service.billingStatus}</div>
                    </div>
                    {service.billingStartDate && (
                      <div>
                        <div>Billing Start Date:</div>
                        <div>{moment(service.billingStartDate).format('DD MMM YYYY')}</div>
                      </div>
                    )}
                    {service.billingEndDate && (
                      <div>
                        <div>Billing End Date:</div>
                        <div>{moment(service.billingEndDate).format('DD MMM YYYY')}</div>
                      </div>
                    )}
                  </SimpleTable>
                </InfoCard>
                <InfoCard title="Line Details" className={sMarg.mb30to15}>
                  <SimpleTable>
                    <div>
                      <div>Usage Plan: </div>
                      <div>{service.usagePlan}</div>
                    </div>
                    {service.currentStatus && (
                      <div>
                        <div>Curent Status: </div>
                        <div>{service.currentStatus}</div>
                      </div>
                    )}
                    {service.contractEnd && (
                      <div>
                        <div>Contract End Date:</div>
                        <div>{moment(service.contractEnd).format('DD MMM YYYY')}</div>
                      </div>
                    )}

                    {service.customerIp && (
                      <div>
                        <div>IP Address:</div>
                        <div>{service.customerIp}</div>
                      </div>
                    )}
                    {service.subnetMask && (
                      <div>
                        <div>Subnet mask:</div>
                        <div>{service.subnetMask}</div>
                      </div>
                    )}
                    {service.gatewayIp && (
                      <div>
                        <div>Gateway:</div>
                        <div>{service.gatewayIp}</div>
                      </div>
                    )}
                    {service.primaryDns && (
                      <div>
                        <div>Primary DNS:</div>
                        <div>{service.primaryDns}</div>
                      </div>
                    )}
                    {service.secondaryDns && (
                      <div>
                        <div>Secondary DNS:</div>
                        <div>{service.secondaryDns}</div>
                      </div>
                    )}
                    {service.cpeType && (
                      <div>
                        <div>Equipment Model:</div>
                        <div>{service.cpeType}</div>
                      </div>
                    )}
                  </SimpleTable>
                </InfoCard>
              </Col>
              <Col className={cn(sLay['flex-gt-xs-40'], 'md')}>
                <StickyChannel>
                  <div className={sLay['layout-column']}>
                    <Button
                      onClick={() =>
                        showCasesCreateTroubleTicket({
                          serviceId: service.id,
                          type: 'internet',
                        })
                      }
                      className={sMarg.mb15}
                      color="secondary"
                      size="sm"
                      rect
                      disabled={inactive}
                      title={inactiveMessage}
                    >
                      Submit a Trouble Ticket
                    </Button>
                    {inactiveMessage && <Tooltip>{inactiveMessage}</Tooltip>}
                    <Button
                      onClick={() =>
                        showNumbersDisconnectModal({
                          serviceId: service.id,
                          type: 'internet',
                        })
                      }
                      className={sMarg.mb15}
                      color="warn"
                      size="sm"
                      rect
                      disabled={inactive}
                      title={inactiveMessage}
                    >
                      Disconnect
                    </Button>
                    {inactiveMessage && <Tooltip>{inactiveMessage}</Tooltip>}
                  </div>
                  <CasesTimeline serviceId={service.id} />
                </StickyChannel>
              </Col>
            </Row>
          )}
        </div>
      </Container>
    </Page>
  )
}



// WEBPACK FOOTER //
// src/pages/InternetService/InternetService.tsx